import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ProductAddSchema } from "../../../ValidationSchema";
import ReactSelect from "../../../Components/PortalSelect";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import AddOptionModal from "./AddOptionModal";
import { alSelectStyles } from "../../../styles/reactSelectTheme";

const colourStyles = alSelectStyles;

const ProductAdd = ({ AddProducts }) => {
  const Navigate = useNavigate();

  const [formState, setFormState] = useState({
    categoryData: [],
    subCategoryData: [],
    allSubCategoryData: [],
    additional_fields: [{ label: "", inputValue: "", values: [] }],
    mediaPreviews: [],
    showAddOptionModal: false,
    addOptionModalField: null,
    isLoading: false,
    validationErrors: {},
    faqs: [{ question: "", answer: "" }],
  });

  // All fields now follow the same pattern
  // const [sizeData, setSizeData] = useState([]);
  const [materialColorData, setMaterialColorData] = useState([
    { label: "White Duplex", value: "White Duplex" },
    { label: "Brown Kraft", value: "Brown Kraft" },
    { label: "Art Paper", value: "Art Paper" },
  ]);
  const [printCoverageData, setPrintCoverageData] = useState([
    { label: "Single", value: "Single" },
    { label: "Multi", value: "Multi" },
    { label: "No print", value: "No print" },
  ]);
  const [paperGsmData, setPaperGsmData] = useState([
    { label: "100gsm", value: "100gsm" },
    { label: "120gsm", value: "120gsm" },
    { label: "150gsm", value: "150gsm" },
    { label: "180gsm", value: "180gsm" },
    { label: "220gsm", value: "220gsm" },
    { label: "above", value: "above" },
  ]);
  const [burstFactorData, setBurstFactorData] = useState([
    { label: "18", value: "18" },
    { label: "20", value: "20" },
    { label: "22", value: "22" },
    { label: "24", value: "24" },
    { label: "28", value: "28" },
    { label: "35", value: "35" },
    { label: "40", value: "40" },
  ]);
  const [noOfPlyData, setNoOfPlyData] = useState([
    { label: "1Ply", value: "1Ply" },
    { label: "2Ply", value: "2Ply" },
    { label: "5Ply", value: "5Ply" },
  ]);

  const [sizeData, setSizeData] = useState([
    { label: "10x10x10", value: "10x10x10" },
    { label: "15x20x15", value: "15x20x15" },
    { label: "20x30x40", value: "20x30x40" },
    { label: "30x40x50", value: "30x40x50" },
  ]);

  const initialValues = {
    title: "",
    size: [],
    material_color: [],
    print_coverage: [],
    paper_gsm: [],
    tags: "",
    slug: "",
    category: "",
    sub_category: "",
    description: "",
    product_details: "",
    date_time: "",
    burst_factor: [],
    no_of_ply: [],
    product_image: [],
    gallery_image_path: "",
    lamination: "",
    quantity: "",
  };

  const updateFormState = (updates) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  };

  // FAQ Functions
  const handleFaqChange = (index, field, value) => {
    const updatedFaqs = [...formState.faqs];
    updatedFaqs[index][field] = value;
    updateFormState({ faqs: updatedFaqs });
  };

  const addFaq = () => {
    updateFormState({
      faqs: [...formState.faqs, { question: "", answer: "" }],
    });
  };

  const removeFaq = (index) => {
    if (formState.faqs.length <= 1) return;
    const updatedFaqs = [...formState.faqs];
    updatedFaqs.splice(index, 1);
    updateFormState({ faqs: updatedFaqs });
  };

  // Unified function to handle adding options to all fields
  const handleAddOption = (field, newOption) => {
    switch (field) {
      case "Size":
        setSizeData((prev) => [...prev, newOption]);
        AddProduct.setFieldValue("size", [
          ...AddProduct.values.size,
          newOption.value,
        ]);
        break;
      case "MaterialColor":
        setMaterialColorData((prev) => [...prev, newOption]);
        AddProduct.setFieldValue("material_color", [
          ...AddProduct.values.material_color,
          newOption.value,
        ]);
        break;
      case "PrintCoverage":
        setPrintCoverageData((prev) => [...prev, newOption]);
        AddProduct.setFieldValue("print_coverage", [
          ...AddProduct.values.print_coverage,
          newOption.value,
        ]);
        break;
      case "PaperGsm":
        setPaperGsmData((prev) => [...prev, newOption]);
        AddProduct.setFieldValue("paper_gsm", [
          ...AddProduct.values.paper_gsm,
          newOption.value,
        ]);
        break;
      case "BurstFactor":
        setBurstFactorData((prev) => [...prev, newOption]);
        AddProduct.setFieldValue("burst_factor", [
          ...AddProduct.values.burst_factor,
          newOption.value,
        ]);
        break;
      case "NoOfPly":
        setNoOfPlyData((prev) => [...prev, newOption]);
        AddProduct.setFieldValue("no_of_ply", [
          ...AddProduct.values.no_of_ply,
          newOption.value,
        ]);
        break;
      default:
        break;
    }
  };

  const openAddOptionModal = (field) => {
    updateFormState({
      showAddOptionModal: true,
      addOptionModalField: field,
    });
  };

  const handleInputChange = (index, field, value) => {
    const updatedFields = [...formState.additional_fields];
    updatedFields[index][field] = value;
    updateFormState({ additional_fields: updatedFields });
  };

  const handleAddRow = () => {
    updateFormState({
      additional_fields: [
        ...formState.additional_fields,
        { label: "", inputValue: "", values: [] },
      ],
    });
  };

  const handleRemoveRow = (index) => {
    if (formState.additional_fields.length <= 1) return;
    const updatedFields = [...formState.additional_fields];
    updatedFields.splice(index, 1);
    updateFormState({ additional_fields: updatedFields });
  };

  const handleAddValue = (e, index) => {
    e.preventDefault();
    const trimmed = formState.additional_fields[index].inputValue.trim();
    if (trimmed === "") return;

    const updatedFields = [...formState.additional_fields];
    updatedFields[index].values.push(trimmed);
    updatedFields[index].inputValue = "";
    updateFormState({ additional_fields: updatedFields });
  };

  const handleRemoveValue = (rowIndex, valIndex) => {
    const updatedFields = [...formState.additional_fields];
    updatedFields[rowIndex].values.splice(valIndex, 1);
    updateFormState({ additional_fields: updatedFields });
  };

  const CategoriesData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/category`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      );

      const categories =
        res?.data?.data?.categories?.map((item) => ({
          label: item?.product_category_name || "Untitled",
          value: item?.product_category_id,
        })) || [];

      updateFormState({ categoryData: categories });
    } catch (err) {
      showErrorToast(err, "Fetching Categories");
    }
  };

  // const SizeData = async () => {
  //   try {
  //     const res = await axios.get(`${process.env.REACT_APP_URL}/size`);

  //     const sizes =
  //       res?.data?.data?.map((item) => ({
  //         label: item?.size || "Untitled",
  //         value: item?.size,
  //       })) || [];

  //     setSizeData(sizes);
  //   } catch (err) {
  //     console.error("Error fetching sizes:", err);
  //     if (err.response?.data?.message === "Unauthorized!") {
  //       Navigate("/");
  //     } else {
  //       toast.error("Failed to load sizes");
  //     }
  //   }
  // };

  const SubCategoriesData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/sub_category`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      );

      const subCategories =
        res?.data?.data?.subcategories?.map((item) => ({
          label: item?.product_subcategory_name || "Untitled",
          value: item?.product_subcategory_id,
          categoryId: item?.product_category_id,
        })) || [];

      updateFormState({
        subCategoryData: subCategories,
        allSubCategoryData: subCategories,
      });
    } catch (err) {
      showErrorToast(err, "Fetching Sub-Categories");
    }
  };

  // 🔹 Local error handler — safe, self-contained
  const showErrorToast = (error, context = "Error") => {
    let message = "Something went wrong.";

    if (error?.response) {
      message =
        error.response.data?.message ||
        error.response.data?.message ||
        `Server error (${error.response.status})`;
    } else if (error?.request) {
      message = "No response from server. Please check your connection.";
    } else if (error?.message) {
      message = error.message;
    }

    toast.error(`${context}: ${message}`, {
      style: {
        border: "1px solid #FCA5A5",
        background: "#FEF2F2",
        color: "#991B1B",
        fontWeight: 500,
        fontSize: "14px",
        padding: "12px 18px",
      },
      icon: "⚠️",
    });

    console.error(`[${context}]`, error);
  };

  const AddProduct = useFormik({
    initialValues,
    validationSchema: ProductAddSchema,
    onSubmit: async (values, action) => {
      const errors = {};
      if (!values.title) errors.title = "Product name is required";
      if (!values.category) errors.category = "Category is required";
      if (!values.product_image || values.product_image.length === 0) {
        errors.product_image = "At least one product image is required";
      }

      if (Object.keys(errors).length > 0) {
        updateFormState({ validationErrors: errors });
        const firstErrorField = Object.keys(errors)[0];
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }

      updateFormState({ validationErrors: {}, isLoading: true });

      try {
        const formData = new FormData();

        // Add all form data
        formData.append("title", values.title);

        // Append array fields correctly - all fields now use the same format
        const arrayFields = [
          "size",
          "material_color",
          "print_coverage",
          "paper_gsm",
          "burst_factor",
          "no_of_ply",
        ];

        arrayFields.forEach((field) => {
          if (values[field] && values[field].length) {
            // Special handling for size field to maintain existing format
            if (field === "size") {
              const formatSize = (size) =>
                Array.isArray(size) ? size.join("x") : size;
              if (values.size.length > 1) {
                formData.append("size", values.size.map(formatSize).join(", "));
              } else {
                formData.append("size", formatSize(values.size[0]));
              }
            } else {
              formData.append(field, values[field].join(", "));
            }
          }
        });

        // Skip blanks rather than posting the string "undefined"/"" for them.
        const appendIfSet = (key, value) => {
          if (value === undefined || value === null || value === "") return;
          formData.append(key, value);
        };

        appendIfSet("tags", values.tags);
        appendIfSet("slug", values.slug);
        formData.append("category", values.category);
        appendIfSet("sub_category", values.sub_category);
        formData.append("description", values.description);
        appendIfSet("product_details", values.product_details);
        appendIfSet("lamination", values.lamination);
        appendIfSet("quantity", values.quantity);

        // Append FAQs as JSON
        formData.append("faqs", JSON.stringify(formState.faqs));

        // Append each file
        values.product_image.forEach((file) => {
          formData.append("product_image", file);
        });

        // Append extra fields
        // 🔹 Combine all additional fields into one JSON array
        const formattedAdditionalFields = formState.additional_fields
          .filter((f) => f.label && f.values.length > 0)
          .map((f) => ({
            label: f.label,
            value: f.values.join(", "),
          }));

        formData.append(
          "additional_fields",
          JSON.stringify(formattedAdditionalFields)
        );

        const response = await axios.post(
          `${process.env.REACT_APP_URL}/admin/products`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
              "Content-Type": "multipart/form-data",
            },
            timeout: 30000,
          }
        );

        if (response.status === 201) {
          action.resetForm();
          updateFormState({
            mediaPreviews: [],
            faqs: [{ question: "", answer: "" }],
            additional_fields: [{ label: "", inputValue: "", values: [] }],
          });
          // The API returns `message`; `msg` was always undefined, so the list
          // page showed no confirmation after a successful add.
          Navigate("/admin/product", {
            state: response.data?.message || "Product added successfully",
          });
        }
      } catch (err) {
        showErrorToast(err, "Adding Product");
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.clear();
          Navigate("/admin/login");
        }
      } finally {
        updateFormState({ isLoading: false });
        action.setSubmitting(false);
      }
    },
  });

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const validFiles = selectedFiles.filter((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    const previews = validFiles.map((file) => ({
      url: URL.createObjectURL(file),
      type: "image",
      name: file.name,
      file,
    }));

    const updatedPreviews = [...formState.mediaPreviews, ...previews];
    const updatedFiles = [...AddProduct.values.product_image, ...validFiles];

    updateFormState({ mediaPreviews: updatedPreviews });
    AddProduct.setFieldValue("product_image", updatedFiles);

    if (formState.validationErrors.product_image) {
      updateFormState((prev) => ({
        ...prev,
        validationErrors: { ...prev.validationErrors, product_image: null },
      }));
    }
  };

  const removeImage = (index) => {
    const updatedPreviews = [...formState.mediaPreviews];
    const updatedFiles = [...AddProduct.values.product_image];

    URL.revokeObjectURL(updatedPreviews[index].url);

    updatedPreviews.splice(index, 1);
    updatedFiles.splice(index, 1);

    updateFormState({ mediaPreviews: updatedPreviews });
    AddProduct.setFieldValue("product_image", updatedFiles);
  };

  useEffect(() => {
    CategoriesData();
    SubCategoriesData();
    // SizeData();

    return () => {
      formState.mediaPreviews.forEach((preview) => {
        URL.revokeObjectURL(preview.url);
      });
    };
  }, []);

  const renderError = (fieldName) => {
    const error =
      AddProduct.errors[fieldName] && AddProduct.touched[fieldName]
        ? AddProduct.errors[fieldName]
        : formState.validationErrors[fieldName];

    return error ? (
      <div className="text-danger mt-2 ml-1 validation-message">{error}</div>
    ) : null;
  };

  return (
    <div className="al">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: { borderRadius: "8px", padding: "12px 20px", fontSize: "14px" },
          success: { icon: "✅", style: { background: "#ECFDF5", color: "#065F46", border: "1px solid #A7F3D0" } },
          error: { icon: "⚠️", style: { background: "#FEF2F2", color: "#991B1B", border: "1px solid #FCA5A5" } },
        }}
      />

      <div className="al-head">
        <div>
          <h1 className="al-title">Add Product</h1>
          <p className="al-sub">Create a new product with specifications, images and FAQs.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)} disabled={formState.isLoading}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <div className="al-card">
        <div className="al-cardhead">
          <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg></div>
          <h3 className="al-cardhead__title">Product Details</h3>
        </div>
            <form onSubmit={AddProduct.handleSubmit}>
              {/* Plain wrapper — the legacy `.form-group` class pulled in
                  structure.css rules (`.form-group input` / `.form-group label`)
                  that outrank `.al-input`/`.al-label`, so this form's inputs and
                  labels rendered with the wrong size, radius and focus ring,
                  unlike every other admin form. The `.row`/`.col` grid handles
                  layout on its own. */}
              <div>
                <div className="row">
                  {/* Product Name */}
                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Product Name <span className="text-danger">*</span>
                    </label>
                    <input
                      className="al-input"
                      type="text"
                      placeholder="Enter Product Name"
                      value={AddProduct.values.title}
                      onChange={AddProduct.handleChange}
                      onBlur={AddProduct.handleBlur}
                      name="title"
                      disabled={formState.isLoading}
                    />
                    {renderError("title")}
                  </div>

                  {/* Category Field */}
                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <ReactSelect
                      options={formState.categoryData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Category"
                      name="category"
                      onChange={(e) => {
                        AddProduct.setFieldValue("category", e?.value);

                        if (e?.value) {
                          const filtered = formState.allSubCategoryData.filter(
                            (sub) => sub.categoryId === e.value
                          );
                          updateFormState({
                            subCategoryData: filtered,
                          });
                        } else {
                          updateFormState({
                            subCategoryData: [],
                          });
                          AddProduct.setFieldValue("sub_category", null);
                        }

                        if (formState.validationErrors.category) {
                          updateFormState((prev) => ({
                            ...prev,
                            validationErrors: {
                              ...prev.validationErrors,
                              category: null,
                            },
                          }));
                        }
                      }}
                      value={
                        formState.categoryData.find(
                          (item) => item.value === AddProduct.values.category
                        ) || null
                      }
                      isDisabled={formState.isLoading}
                    />
                    {renderError("category")}
                  </div>

                  {/* Sub-Category Field */}
                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Sub-Category
                    </label>
                    <ReactSelect
                      options={formState.subCategoryData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Sub-Category"
                      name="sub_category"
                      onChange={(e) => {
                        AddProduct.setFieldValue("sub_category", e?.value);
                      }}
                      value={
                        formState.subCategoryData.find(
                          (item) =>
                            item.value === AddProduct.values.sub_category
                        ) || null
                      }
                      isDisabled={
                        formState.isLoading || !AddProduct.values.category
                      }
                    />
                  </div>

                  {/* Size Field - Now consistent with other fields */}
                  {/* Size Field - Now Static */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Size
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("Size")}
                        title="Add New Size"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={sizeData} // ✅ static list
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Size"
                      name="size"
                      onChange={(selectedOptions) =>
                        AddProduct.setFieldValue(
                          "size",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={sizeData.filter((option) =>
                        AddProduct.values.size?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("size")}
                  </div>

                  {/* Material Color Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Material Color
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("MaterialColor")}
                        title="Add New Material Color"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={materialColorData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Material Color"
                      name="material_color"
                      onChange={(selectedOptions) =>
                        AddProduct.setFieldValue(
                          "material_color",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={materialColorData.filter((option) =>
                        AddProduct.values.material_color?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("material_color")}
                  </div>

                  {/* Print Coverage Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Print Coverage
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("PrintCoverage")}
                        title="Add New Print Coverage"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={printCoverageData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Print Coverage"
                      name="print_coverage"
                      onChange={(selectedOptions) =>
                        AddProduct.setFieldValue(
                          "print_coverage",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={printCoverageData.filter((option) =>
                        AddProduct.values.print_coverage?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("print_coverage")}
                  </div>

                  {/* Paper GSM Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Paper GSM
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("PaperGsm")}
                        title="Add New Paper GSM"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={paperGsmData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Paper GSM"
                      name="paper_gsm"
                      onChange={(selectedOptions) =>
                        AddProduct.setFieldValue(
                          "paper_gsm",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={paperGsmData.filter((option) =>
                        AddProduct.values.paper_gsm?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("paper_gsm")}
                  </div>

                  {/* Burst Factor Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Burst Factor
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("BurstFactor")}
                        title="Add New Burst Factor"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={burstFactorData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Burst Factor"
                      name="burst_factor"
                      onChange={(selectedOptions) =>
                        AddProduct.setFieldValue(
                          "burst_factor",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={burstFactorData.filter((option) =>
                        AddProduct.values.burst_factor?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("burst_factor")}
                  </div>

                  {/* No Of Ply Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      No Of Ply
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("NoOfPly")}
                        title="Add New No Of Ply"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={noOfPlyData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Ply"
                      name="no_of_ply"
                      onChange={(selectedOptions) =>
                        AddProduct.setFieldValue(
                          "no_of_ply",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={noOfPlyData.filter((option) =>
                        AddProduct.values.no_of_ply?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("no_of_ply")}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Lamination
                    </label>
                    <ReactSelect
                      options={[
                        { label: "Yes", value: "Yes" },
                        { label: "No", value: "No" },
                      ]}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Lamination"
                      name="lamination"
                      onChange={(e) =>
                        AddProduct.setFieldValue("lamination", e?.value)
                      }
                    />
                    {AddProduct.errors.lamination &&
                      AddProduct.touched.lamination && (
                        <h6 className="text-danger mt-2 ml-1">
                          {AddProduct.errors.lamination}
                        </h6>
                      )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Quantity
                    </label>
                    <input
                      className="al-input"
                      type="text"
                      placeholder="Enter Quantity"
                      value={AddProduct.values.quantity}
                      onChange={AddProduct.handleChange}
                      onBlur={AddProduct.handleBlur}
                      name="quantity"
                    />
                  </div>

                  {/* Product Image Field */}
                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Product Image <span className="text-danger">*</span>
                    </label>
                    <input
                      className="al-input al-input--file"
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                      onBlur={AddProduct.handleBlur}
                      name="product_image"
                      disabled={formState.isLoading}
                    />
                    {renderError("product_image")}

                    {/* Image Previews */}
                    <div className="mt-3">
                      <div className="d-flex flex-wrap gap-2">
                        {formState.mediaPreviews.map((preview, index) => (
                          <div
                            key={index}
                            className="position-relative"
                            style={{ width: "100px", height: "100px" }}
                          >
                            <img
                              src={preview.url}
                              alt={`Preview ${index + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "8px",
                                border: "1px solid var(--color-border)",
                              }}
                            />
                            <button
                              type="button"
                              className="al-imgx"
                              onClick={() => removeImage(index)}
                              disabled={formState.isLoading}
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                      {formState.mediaPreviews.length > 0 && (
                        <small className="text-muted">
                          {formState.mediaPreviews.length} image(s) selected
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Tags
                    </label>
                    <input
                      className="al-input"
                      type="text"
                      placeholder="Enter Tags (comma separated)"
                      value={AddProduct.values.tags}
                      onChange={(e) =>
                        AddProduct.setFieldValue("tags", e.target.value)
                      }
                      onBlur={AddProduct.handleBlur}
                      name="tags"
                    />
                    {AddProduct.errors.tags && AddProduct.touched.tags && (
                      <h6 className="text-danger mt-2 ml-1">
                        {AddProduct.errors.tags}
                      </h6>
                    )}
                  </div>
                </div>
                {/* Product Details Field */}
                {/* <div className="form-group mb-4">
                  <label className="al-label">
                    Product Details
                  </label>
                  <textarea
                    className="al-input"
                    rows="5"
                    placeholder="Enter product details"
                    value={AddProduct.values.product_details}
                    onChange={AddProduct.handleChange}
                    onBlur={AddProduct.handleBlur}
                    name="product_details"
                    disabled={formState.isLoading}
                  ></textarea>
                  {renderError("product_details")}
                </div> */}

                {/* Additional Fields Section */}
                <div className="al-subpanel">
                  <div className="al-subpanel__head">
                    <span className="al-subpanel__title">Additional Fields</span>
                    <button
                      type="button"
                      className="al-btn al-btn--sm"
                      onClick={handleAddRow}
                      disabled={formState.isLoading}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      Add Field
                    </button>
                  </div>

                  {formState.additional_fields.map((field, index) => (
                    <div className="al-fieldrow" key={index}>
                      <div className="al-fieldrow__main">
                        <input
                          type="text"
                          className="al-input al-fieldrow__label"
                          placeholder="Field label"
                          value={field.label}
                          onChange={(e) =>
                            handleInputChange(index, "label", e.target.value)
                          }
                          disabled={formState.isLoading}
                        />

                        <div className="al-fieldrow__value">
                          <input
                            type="text"
                            className="al-input"
                            placeholder="Enter value"
                            value={field.inputValue}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "inputValue",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddValue(e, index);
                              }
                            }}
                            disabled={formState.isLoading}
                          />
                          <button
                            type="button"
                            className="al-btn al-btn--primary al-btn--sm"
                            onClick={(e) => handleAddValue(e, index)}
                            disabled={formState.isLoading}
                          >
                            Add
                          </button>
                        </div>

                        <button
                          type="button"
                          className="al-btn al-btn--sm al-btn--danger"
                          onClick={() => handleRemoveRow(index)}
                          disabled={
                            formState.isLoading ||
                            formState.additional_fields.length <= 1
                          }
                        >
                          Remove
                        </button>
                      </div>

                      {field.values.length > 0 && (
                        <div className="al-fieldrow__chips">
                          {field.values.map((val, valIndex) => (
                            <div key={valIndex} className="al-chip">
                              {val}
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveValue(index, valIndex)
                                }
                                className="al-chip__x"
                                disabled={formState.isLoading}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <label className="al-label">
                    Description
                  </label>

                  <CKEditor
                    editor={ClassicEditor}
                    data={AddProduct.values.product_details}
                    className="custom-ckeditor"
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      AddProduct.setFieldValue("product_details", data);
                    }}
                    onBlur={() =>
                      AddProduct.setFieldTouched("product_details", true)
                    }
                    disabled={formState.isLoading}
                  />

                  {renderError("product_details")}
                </div>

                {/* Description Field */}
                <div className="mb-4">
                  <label className="al-label">
                    Specifications
                  </label>

                  <CKEditor
                    editor={ClassicEditor}
                    data={AddProduct.values.description}
                    className="custom-ckeditor"
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      AddProduct.setFieldValue("description", data);
                    }}
                    onBlur={() =>
                      AddProduct.setFieldTouched("description", true)
                    }
                    disabled={formState.isLoading}
                  />

                  {renderError("description")}
                </div>

                {/* FAQ Section */}
                <div className="al-subpanel">
                  <div className="al-subpanel__head">
                    <span className="al-subpanel__title">Frequently Asked Questions</span>
                    <button
                      type="button"
                      className="al-btn al-btn--sm"
                      onClick={addFaq}
                      disabled={formState.isLoading}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      Add FAQ
                    </button>
                  </div>

                  {formState.faqs.map((faq, index) => (
                    <div className="row mb-3" key={index}>
                      <div className="col-md-5 mb-2">
                        <input
                          type="text"
                          className="al-input"
                          placeholder="Question"
                          value={faq.question}
                          onChange={(e) =>
                            handleFaqChange(index, "question", e.target.value)
                          }
                          disabled={formState.isLoading}
                        />
                      </div>
                      <div className="col-md-5 mb-2">
                        <input
                          type="text"
                          className="al-input"
                          placeholder="Answer"
                          value={faq.answer}
                          onChange={(e) =>
                            handleFaqChange(index, "answer", e.target.value)
                          }
                          disabled={formState.isLoading}
                        />
                      </div>
                      <div className="col-md-2 mb-2 d-flex align-items-center">
                        <button
                          type="button"
                          className="al-btn al-btn--sm al-btn--danger"
                          onClick={() => removeFaq(index)}
                          disabled={
                            formState.faqs.length <= 1 || formState.isLoading
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add Option Modal */}
              <AddOptionModal
                show={formState.showAddOptionModal}
                onClose={() => updateFormState({ showAddOptionModal: false })}
                onAddOption={(newOption) => {
                  handleAddOption(formState.addOptionModalField, newOption);
                }}
              />

              <div className="al-actionbar">
                <button type="button" className="al-btn" onClick={() => Navigate(-1)} disabled={formState.isLoading}>Cancel</button>
                <button
                  type="submit"
                  className="al-btn al-btn--primary"
                  disabled={AddProduct.isSubmitting || formState.isLoading}
                >
                  {formState.isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Adding Product...
                    </>
                  ) : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      Create Product
                    </>
                  )}
                </button>
              </div>
            </form>
      </div>
    </div>
  );
};

export default ProductAdd;
